import React, { useContext, useState, useEffect } from 'react';
import { InputText } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useGetTranslations } from '@/generated/translations';
import styles from '../ApplicationDetail.module.scss';

interface LinkedTemperaturesProps {
	onChange: () => void;
	defaultMin?: number | null;
	defaultMax?: number | null;
	defaultNorm?: number | null;
	hasNorm?: boolean;
	id: string;
}

export const LinkedTemperatures: React.FunctionComponent<LinkedTemperaturesProps> = ({
	onChange,
	defaultMin,
	defaultMax,
	defaultNorm,
	hasNorm,
	id,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);

	const [minValue, setMinValue] = useState(typeof defaultMin === 'number' ? defaultMin : undefined);
	const [maxValue, setMaxValue] = useState(typeof defaultMax === 'number' ? defaultMax : undefined);
	const [normValue, setNormValue] = useState(typeof defaultNorm === 'number' ? defaultNorm : undefined);
	const [minError, setMinError] = useState<string | undefined>(undefined);
	const [maxError, setMaxError] = useState<string | undefined>(undefined);
	const [normError, setNormError] = useState<string | undefined>(undefined);

	const onChangeMin = (e) => {
		const value = e.target.value ? Number(e.target.value) : undefined;
		setMinValue(value);
		if (
			(hasNorm && typeof normValue === 'number' && typeof value === 'number' && value > normValue) ||
			(typeof maxValue === 'number' && typeof value === 'number' && value > maxValue)
		) {
			setMinError(translations?.data.applications.minError);
		} else {
			if (maxError || normError) {
				setMaxError(undefined);
			}
			setMinError(undefined);
			onChange();
		}
	};

	const onChangeMax = (e) => {
		const value = e.target.value ? Number(e.target.value) : undefined;
		setMaxValue(value);
		if (
			(hasNorm && typeof normValue === 'number' && typeof value === 'number' && value < normValue) ||
			(typeof minValue === 'number' && typeof value === 'number' && value < minValue)
		) {
			setMaxError(translations?.data.applications.maxError);
		} else {
			if (minError || normError) {
				setMinError(undefined);
			}
			setMaxError(undefined);
			onChange();
		}
	};

	const onChangeNorm = (e) => {
		const value = e.target.value ? Number(e.target.value) : undefined;
		setNormValue(value);
	};

	useEffect(() => {
		if (
			(typeof minValue === 'number' && typeof normValue === 'number' && normValue < minValue) ||
			(typeof maxValue === 'number' && typeof normValue === 'number' && normValue > maxValue)
		) {
			setNormError(translations?.data.applications.normError);
		} else {
			setNormError(undefined);
			onChange();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [minValue, maxValue, normValue]);

	return (
		<div className={styles.flex}>
			<InputText
				name={`min${id}`}
				id={`min${id}`}
				value={minValue ?? ''}
				label={translations?.data.applications[`min${id}`]}
				placeholder={translations?.data.applications.min}
				onChange={onChangeMin}
				variant={'inverse'}
				type="number"
				hideLabel
				errorText={minError}
				suffix={translations?.data.applications.temperatureUnit}
				isSmall
			/>
			{hasNorm && (
				<InputText
					name={`normal${id}`}
					id={`normal${id}`}
					value={normValue ?? ''}
					label={translations?.data.applications[`normal${id}`]}
					placeholder={translations?.data.applications.normal}
					onChange={onChangeNorm}
					variant={'inverse'}
					type="number"
					errorText={normError}
					hideLabel
					suffix={translations?.data.applications.temperatureUnit}
					isSmall
				/>
			)}
			<InputText
				name={`max${id}`}
				id={`max${id}`}
				value={maxValue ?? ''}
				label={translations?.data.applications[`max${id}`]}
				placeholder={translations?.data.applications.max}
				onChange={onChangeMax}
				variant={'inverse'}
				type="number"
				errorText={maxError}
				hideLabel
				suffix={translations?.data.applications.temperatureUnit}
				isSmall
			/>
		</div>
	);
};
